/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';

export type ConnectionState = 'online' | 'offline' | 'degraded' | 'recovering';

interface ConnectionStatus {
  state: ConnectionState;
  isOnline: boolean;
  degradedServices: string[];
  lastOnlineAt: Date | null;
}

interface ConnectionStatusContextValue {
  status: ConnectionStatus;
  markServiceDegraded: (serviceName: string) => void;
  markServiceRecovered: (serviceName: string) => void;
  checkConnection: () => Promise<boolean>;
}

const ConnectionStatusContext = createContext<ConnectionStatusContextValue | null>(null);

interface ConnectionStatusProviderProps {
  children: ReactNode;
  /** Optional toast function - if not provided, toasts are skipped */
  showToast?: (toast: { type: 'success' | 'warning'; message: string }) => void;
}

export function ConnectionStatusProvider({ children, showToast }: ConnectionStatusProviderProps) {
  const [status, setStatus] = useState<ConnectionStatus>(() => ({
    state: typeof navigator !== 'undefined' && navigator.onLine ? 'online' : 'offline',
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    degradedServices: [],
    lastOnlineAt: typeof navigator !== 'undefined' && navigator.onLine ? new Date() : null,
  }));

  // Track if we've shown the offline toast to avoid duplicates
  const hasShownOfflineToastRef = useRef(false);

  // Handle browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setStatus((prev) => ({
        ...prev,
        state: prev.degradedServices.length > 0 ? 'degraded' : 'online',
        isOnline: true,
        lastOnlineAt: new Date(),
      }));
      hasShownOfflineToastRef.current = false;
      showToast?.({ type: 'success', message: 'Connection restored' });
    };

    const handleOffline = () => {
      setStatus((prev) => ({
        ...prev,
        state: 'offline',
        isOnline: false,
      }));
      if (!hasShownOfflineToastRef.current) {
        hasShownOfflineToastRef.current = true;
        showToast?.({ type: 'warning', message: 'You appear to be offline' });
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [showToast]);

  const markServiceDegraded = useCallback((serviceName: string) => {
    setStatus((prev) => {
      if (prev.degradedServices.includes(serviceName)) return prev;
      const newDegraded = [...prev.degradedServices, serviceName];
      return {
        ...prev,
        state: prev.isOnline ? 'degraded' : 'offline',
        degradedServices: newDegraded,
      };
    });
  }, []);

  const markServiceRecovered = useCallback((serviceName: string) => {
    setStatus((prev) => {
      const newDegraded = prev.degradedServices.filter((s) => s !== serviceName);
      return {
        ...prev,
        state: prev.isOnline && newDegraded.length === 0 ? 'online' : prev.state,
        degradedServices: newDegraded,
      };
    });
  }, []);

  const checkConnection = useCallback(async (): Promise<boolean> => {
    try {
      // Simple connectivity check using a lightweight asset
      const response = await fetch('/favicon.svg', {
        method: 'HEAD',
        cache: 'no-store',
      });
      return response.ok;
    } catch {
      return false;
    }
  }, []);

  const value = useMemo(
    () => ({
      status,
      markServiceDegraded,
      markServiceRecovered,
      checkConnection,
    }),
    [status, markServiceDegraded, markServiceRecovered, checkConnection]
  );

  return (
    <ConnectionStatusContext.Provider value={value}>{children}</ConnectionStatusContext.Provider>
  );
}

export function useConnectionStatus(): ConnectionStatusContextValue {
  const context = useContext(ConnectionStatusContext);
  if (!context) {
    throw new Error('useConnectionStatus must be used within a ConnectionStatusProvider');
  }
  return context;
}

/**
 * Optional hook that returns connection status or null if outside provider.
 * Useful for components that may render before provider is mounted.
 */
export function useConnectionStatusOptional(): ConnectionStatusContextValue | null {
  return useContext(ConnectionStatusContext);
}
